import api from './api';
import { Rechnung, RechnungAbsender, RechnungCreate, RechnungList, RechnungTextvorlage, ZeiteintragAbweichung, ZeiteintragSyncAllResult, ZeiteintragSyncResult } from '../types';

interface RechnungListParams {
  projekt_id?: number;
  status?: string;
}

/**
 * Fehlerdetails aus einer Blob-Antwort lesen.
 *
 * Bei responseType 'blob' liefert Axios auch Fehlerantworten als Blob, sodass
 * die Meldung des Backends (etwa fehlende Stammdaten) sonst verloren geht.
 */
const readBlobErrorDetail = async (error: unknown): Promise<string | null> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text());
    return typeof parsed?.detail === 'string' ? parsed.detail : null;
  } catch {
    return null;
  }
};

const downloadBlob = (data: Blob, contentDisposition: string | undefined, fallbackName: string) => {
  const filename = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] || fallbackName;
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const rechnungenService = {
  list: async (params?: RechnungListParams): Promise<RechnungList[]> => {
    const response = await api.get('/rechnungen/', { params });
    return response.data;
  },

  get: async (id: number): Promise<Rechnung> => {
    const response = await api.get(`/rechnungen/${id}`);
    return response.data;
  },

  create: async (data: RechnungCreate): Promise<Rechnung> => {
    const response = await api.post('/rechnungen/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<RechnungCreate>): Promise<Rechnung> => {
    const response = await api.put(`/rechnungen/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/rechnungen/${id}`);
  },

  updateStatus: async (id: number, status: string): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/status`, { status });
    return response.data;
  },

  generatePdf: async (id: number): Promise<void> => {
    const response = await api.get(`/rechnungen/${id}/pdf`, {
      responseType: 'blob',
      params: { _: Date.now() },
    });
    downloadBlob(response.data, response.headers['content-disposition'], `rechnung-${id}.pdf`);
  },

  generateDocx: async (id: number): Promise<void> => {
    const response = await api.get(`/rechnungen/${id}/docx`, {
      responseType: 'blob',
      params: { _: Date.now() },
    });
    downloadBlob(response.data, response.headers['content-disposition'], `rechnung-${id}.docx`);
  },

  generateZugferdPdf: async (id: number): Promise<void> => {
    try {
      const response = await api.get(`/rechnungen/${id}/zugferd`, {
        responseType: 'blob',
        params: { _: Date.now() },
      });
      downloadBlob(
        response.data,
        response.headers['content-disposition'],
        `rechnung-${id}-zugferd.pdf`,
      );
    } catch (error) {
      const detail = await readBlobErrorDetail(error);
      throw detail ? new Error(detail) : error;
    }
  },

  generateZugferdXml: async (id: number): Promise<void> => {
    try {
      const response = await api.get(`/rechnungen/${id}/zugferd/xml`, {
        responseType: 'blob',
        params: { _: Date.now() },
      });
      downloadBlob(
        response.data,
        response.headers['content-disposition'],
        `rechnung-${id}-factur-x.xml`,
      );
    } catch (error) {
      const detail = await readBlobErrorDetail(error);
      throw detail ? new Error(detail) : error;
    }
  },

  generateFromZeiteintraege: async (
    id: number,
    zeitraum?: { leistungszeitraum_von: string; leistungszeitraum_bis: string },
  ): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/aus-zeiteintraegen`, null, {
      params: zeitraum,
    });
    return response.data;
  },

  getNextNumber: async (): Promise<string> => {
    const response = await api.get('/rechnungen/naechste-nummer');
    return response.data.rechnungsnummer;
  },

  getOffeneMonate: async (projektId: number): Promise<{ monat: string; stunden: number; anzahl: number }[]> => {
    const response = await api.get('/zeiteintraege/offene-monate', { params: { projekt_id: projektId } });
    return response.data;
  },

  schnellabrechnung: async (projektId: number, monat: string): Promise<Rechnung> => {
    const response = await api.post('/rechnungen/schnellabrechnung', null, {
      params: { projekt_id: projektId, monat },
    });
    return response.data;
  },

  getEinstellungen: async (): Promise<RechnungAbsender> => {
    const response = await api.get('/rechnungen/einstellungen');
    return response.data;
  },

  updateEinstellungen: async (data: Partial<RechnungAbsender>): Promise<RechnungAbsender> => {
    const response = await api.put('/rechnungen/einstellungen', data);
    return response.data;
  },

  aufteilen: async (
    id: number,
    anteile: { kunde_id: number; bezeichnung: string; prozent: number }[],
  ): Promise<{ rechnungen: Rechnung[] }> => {
    const response = await api.post(`/rechnungen/${id}/aufteilen`, { anteile });
    return response.data;
  },

  listTextvorlagen: async (): Promise<RechnungTextvorlage[]> => {
    const response = await api.get('/rechnungen/textvorlagen');
    return response.data;
  },

  createTextvorlage: async (data: Omit<RechnungTextvorlage, 'id'>): Promise<RechnungTextvorlage> => {
    const response = await api.post('/rechnungen/textvorlagen', data);
    return response.data;
  },

  updateTextvorlage: async (
    id: number,
    data: Partial<Omit<RechnungTextvorlage, 'id'>>,
  ): Promise<RechnungTextvorlage> => {
    const response = await api.put(`/rechnungen/textvorlagen/${id}`, data);
    return response.data;
  },

  deleteTextvorlage: async (id: number): Promise<void> => {
    await api.delete(`/rechnungen/textvorlagen/${id}`);
  },

  listZeiteintragAbweichungen: async (): Promise<ZeiteintragAbweichung[]> => {
    const response = await api.get('/rechnungen/zeiteintraege-abweichungen');
    return response.data;
  },

  syncZeiteintraege: async (id: number): Promise<ZeiteintragSyncResult> => {
    const response = await api.post(`/rechnungen/${id}/zeiteintraege/sync`);
    return response.data;
  },

  syncAlleZeiteintragAbweichungen: async (): Promise<ZeiteintragSyncAllResult> => {
    const response = await api.post('/rechnungen/zeiteintraege-abweichungen/sync');
    return response.data;
  },
};

export default rechnungenService;
